import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

const Combobox = ({ 
  items, 
  name, 
  default: defaultText, 
  currentState, 
  onChange,
  className 
}) => {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentState);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredItems = items.filter(item =>
    item.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const event = {
      target: {
        name: name,
        value: ''
      }
    };
    
    setValue('');
    setSearchQuery('');
    onChange(event);
  };

  const handleSelect = (selectedValue) => {
    const event = {
      target: {
        name: name,
        value: selectedValue
      }
    };
    setValue(selectedValue);
    setSearchQuery('');
    onChange(event);
    setOpen(false);
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full px-4 py-2 text-left bg-white border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      >
        <span className="truncate">
          {value 
            ? items.find(item => item.value === value)?.label 
            : defaultText}
        </span>
        {value && (
          <button
            onClick={handleClear}
            className="absolute right-8 top-1/2 transform -translate-y-1/2"
          >
            <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </button>
        )}
        <span className="absolute inset-y-0 right-0 flex items-center pr-2">
          <svg 
            className="h-5 w-5 text-gray-400" 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path 
              fillRule="evenodd" 
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" 
              clipRule="evenodd" 
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-md shadow-lg">
          <div className="p-2">
            <input
              ref={inputRef}
              type="text"
              className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={defaultText}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
          </div>
          
          <ul className="max-h-60 overflow-auto py-1">
            {filteredItems.length === 0 ? (
              <li className="px-4 py-2 text-sm text-gray-500">
                None found.
              </li>
            ) : (
              filteredItems.map((item) => (
                <li
                  key={item.value}
                  onClick={() => handleSelect(item.value)}
                  className="px-4 py-2 text-sm hover:bg-blue-50 cursor-pointer"
                >
                  {item.label}
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Combobox;